import React, { useEffect, useState } from 'react'
import { Box, Card, CardContent, CircularProgress, Typography, useTheme, alpha } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { smbsApi } from '../../services/api'

interface TotalAiReplyWidgetProps {
    smbId: string
}

export const TotalAiReplyWidget: React.FC<TotalAiReplyWidgetProps> = ({ smbId }) => {
    const [count, setCount] = useState<number | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const theme = useTheme()

    useEffect(() => {
        const fetchAiRepliesCount = async () => {
            try {
                setLoading(true)
                setError(false)
                // Calculate exactly 30 days ago and now
                const endDate = new Date()
                const startDate = new Date()
                startDate.setDate(endDate.getDate() - 30)

                const data = await smbsApi.getAiRepliesCount(
                    smbId, 
                    startDate.toISOString(), 
                    endDate.toISOString()
                )
                setCount(data.count)
            } catch (err) {
                console.error('Failed to fetch AI replies count:', err)
                setError(true)
                setCount(null)
            } finally {
                setLoading(false)
            }
        }

        if (smbId) {
            fetchAiRepliesCount()
        }
    }, [smbId])

    if (loading) {
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </CardContent>
            </Card>
        )
    }

    if (error) {
        return (
            <Card 
                sx={{ 
                    mb: 2, 
                    border: `1px solid ${theme.palette.error.light}`,
                    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`,
                }}
            >
                <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <AutoAwesomeIcon color="error" fontSize="medium" />
                    </Box>
                    
                    <Box>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold', lineHeight: 1.2, display: 'block', mb: 0.5 }}>
                            Total AI Messages (Last 30 Days)
                        </Typography>
                        
                        <Typography
                            variant="body2"
                            color="error.main"
                            sx={{ 
                                fontFamily: "'Inter', 'Roboto', 'Helvetica Neue', sans-serif",
                                fontWeight: 'medium'
                            }}
                        >
                            Failed to load
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        )
    }

    if (count === null) {
        return null
    }

    return (
        <Card 
            sx={{ 
                mb: 2, 
                border: `1px solid ${theme.palette.divider}`,
                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
            }}
        >
            <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box 
                    sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <AutoAwesomeIcon color="primary" fontSize="medium" />
                </Box>
                
                <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold', lineHeight: 1.2, display: 'block', mb: 0.5 }}>
                        Total AI Messages (Last 30 Days)
                    </Typography>
                    
                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        color="text.primary"
                        sx={{ 
                            fontFamily: "'Inter', 'Roboto', 'Helvetica Neue', sans-serif",
                            letterSpacing: '-0.5px',
                        }}
                    >
                        {count.toLocaleString()}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    )
}
